package com.bikompanion.ui.home

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Button
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import dev.chrisbanes.haze.HazeState
import com.bikompanion.ui.composables.GlassToastSurface
import com.bikompanion.ui.install.AppInstallOffer
import com.bikompanion.ui.install.hint
import com.bikompanion.ui.install.icon
import com.bikompanion.ui.preview.GlassPreviewSurface
import com.bikompanion.ui.strings.LocalStrings

// The nav bar's source layer, so an open sheet shows the toast through its glass.
private const val AppInstallToastHazeZIndex = 1f

private val InstallButtonHeight = 36.dp
private val InstallButtonPadding = PaddingValues(horizontal = 16.dp)
private val DismissButtonSize = 32.dp
private val DismissIconSize = 18.dp

/**
 * Offers the app until the rider dismisses it. Unlike `GlassToast`, this stays and takes input.
 *
 * Must be a sibling of [hazeState]'s source content, for the reason given on `HomeNavigationBar`.
 */
@Composable
internal fun AppInstallToast(
    visible: Boolean,
    offer: AppInstallOffer,
    hazeState: HazeState,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    AnimatedVisibility(
        visible = visible,
        enter = fadeIn(),
        exit = fadeOut(),
        modifier = modifier,
    ) {
        AppInstallToastContent(offer = offer, hazeState = hazeState, onDismiss = onDismiss)
    }
}

@Composable
private fun AppInstallToastContent(
    offer: AppInstallOffer,
    hazeState: HazeState,
    onDismiss: () -> Unit,
) {
    val strings = LocalStrings.current
    val uriHandler = LocalUriHandler.current
    GlassToastSurface(
        hazeState = hazeState,
        hazeSourceZIndex = AppInstallToastHazeZIndex,
        modifier = Modifier.widthIn(max = NavMaxWidth).fillMaxWidth(),
    ) {
        Icon(
            imageVector = offer.icon,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.primary,
        )
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = strings.installTitle,
                style = MaterialTheme.typography.labelLarge,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = offer.hint(strings),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        if (offer is AppInstallOffer.OpenStore) {
            Button(
                onClick = { uriHandler.openUri(offer.url) },
                contentPadding = InstallButtonPadding,
                modifier = Modifier.height(InstallButtonHeight),
            ) {
                Text(strings.actionInstall)
            }
        }
        IconButton(onClick = onDismiss, modifier = Modifier.size(DismissButtonSize)) {
            Icon(
                imageVector = Icons.Default.Close,
                contentDescription = strings.actionClose,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
                modifier = Modifier.size(DismissIconSize),
            )
        }
    }
}

@Preview
@Composable
private fun AppInstallToastIosPreview() {
    GlassPreviewSurface { hazeState ->
        AppInstallToastContent(
            offer = AppInstallOffer.AddToHomeScreen,
            hazeState = hazeState,
            onDismiss = {},
        )
    }
}

@Preview
@Composable
private fun AppInstallToastAndroidPreview() {
    GlassPreviewSurface { hazeState ->
        AppInstallToastContent(
            offer = AppInstallOffer.OpenStore("https://play.google.com/"),
            hazeState = hazeState,
            onDismiss = {},
        )
    }
}
